import React, { useState, useEffect, useRef, useCallback } from 'react';
import QuickExercises from './QuickExercises';
import LearningCards from './LearningCards';
import SyntaxGuessCard from './SyntaxGuessCard';
import CodingContentViewer from './CodingContentViewer';
import { interactiveLearningService } from '../../services/interactiveLearningService';
import { activityLogService } from '../../services/activityLogService';
import { runPlaygroundCode } from '../../utils/playgroundRunner';
import { mergeSessionFeatureToggles } from '../../config/sessionFeatureToggles';
import './TopicInteractiveLearning.css';

const ACTIVITY_ORDER = ['slides', 'quiz', 'exercises', 'matching', 'syntax', 'code'];

const ACTIVITY_CONFIG = {
  quiz: {
    label: 'Quick Quiz',
    icon: '🎯',
    description: 'Fast knowledge checks with instant feedback.'
  },
  exercises: {
    label: 'Quick Exercises',
    icon: '⚡',
    description: 'Short practice prompts to reinforce the lesson.'
  },
  matching: {
    label: 'Matching',
    icon: '🔗',
    description: 'Drag concepts to their correct partners.'
  },
  syntax: {
    label: 'Syntax Sprint',
    icon: '⌛',
    description: 'Beat the clock by fixing syntax cards.'
  },
  code: {
    label: 'Code Playground',
    icon: '💻',
    description: 'Write and run code samples right here.'
  },
  coding: {
    label: 'Coding Content',
    icon: '💻',
    description: 'View coding examples and learn from instructor code.'
  },
  slides: {
    label: 'Presentation Deck',
    icon: '🖼️',
    description: 'Coach-curated slides for the topic.'
  }
};

const TopicInteractiveLearning = ({ userId, userName, topic, courseId, onProgressUpdate, sessionFeatureToggles, isStudent = false }) => {
  const [activeActivity, setActiveActivity] = useState(null);
  const [score, setScore] = useState(0);
  const [customContent, setCustomContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(true);
  const previousScoreRef = useRef(0);
  const featureToggles = mergeSessionFeatureToggles(sessionFeatureToggles);

  const getActivityAvailability = useCallback(() => {
    // Check for coding examples (new array format or old single format)
    const hasCodingExamples = customContent?.codingExamples?.length > 0;
    const hasEnabledCodingExamples = hasCodingExamples && 
      customContent.codingExamples.some(ex => ex.enabled !== false);
    const hasOldCoding = customContent?.coding?.code && customContent?.coding?.enabled !== false;
    
    // For students, only show coding content
    if (isStudent) {
      return {
        slides: false,
        quiz: false,
        exercises: false,
        matching: false,
        syntax: false,
        code: false,
        coding: hasEnabledCodingExamples || hasOldCoding
      };
    }
    
    // For admin/coach, show all activities
    return {
      slides: featureToggles.enablePresentationDeck && Boolean(customContent?.presentationSlides?.length),
      quiz: featureToggles.enableQuickQuiz,
      exercises: featureToggles.enableQuickExercises,
      matching: featureToggles.enableMatching,
      syntax:
        featureToggles.enableSyntaxGuess && Boolean(customContent?.syntaxGuessCards?.length || topic?.name),
      code:
        featureToggles.enableCodePlayground && Boolean(topic?.sampleCode || customContent?.codeExamples?.length),
      coding: hasEnabledCodingExamples || hasOldCoding
    };
  }, [featureToggles, customContent, topic, isStudent]);

  // Load custom content from Firestore
  useEffect(() => {
    const loadContent = async () => {
      if (!courseId || !topic?.name) {
        setLoadingContent(false);
        return;
      }
      
      try {
        const content = await interactiveLearningService.getTopicContent(courseId, topic.name);
        setCustomContent(content);
        // Auto-show coding content if available
        const hasCoding = (content?.codingExamples?.length > 0 && 
          content.codingExamples.some(ex => ex.enabled !== false)) ||
          (content?.coding?.code && content?.coding?.enabled !== false);
        if (hasCoding) {
          setActiveActivity('coding');
        }
      } catch (error) {
        console.debug('No custom content found, using defaults');
        setCustomContent(null);
      } finally {
        setLoadingContent(false);
      }
    };
    
    loadContent();
  }, [courseId, topic?.name]);

  // Update parent component when score changes
  useEffect(() => {
    if (score > previousScoreRef.current && onProgressUpdate) {
      const newStars = score - previousScoreRef.current;
      onProgressUpdate(newStars);
      previousScoreRef.current = score;
    }
  }, [score, onProgressUpdate]);

  useEffect(() => {
    if (loadingContent || activeActivity) return;
    const availability = getActivityAvailability();
    const nextActivity = ACTIVITY_ORDER.find((key) => availability[key]);
    if (nextActivity) {
      setActiveActivity(nextActivity);
    }
  }, [loadingContent, customContent, featureToggles, topic, activeActivity, getActivityAvailability]);

  useEffect(() => {
    setActiveActivity(null);
  }, [topic?.name, courseId]);

  useEffect(() => {
    if (!activeActivity) return;
    const availability = getActivityAvailability();
    if (availability[activeActivity]) return;
    const fallback = ACTIVITY_ORDER.find((key) => availability[key]) || null;
    setActiveActivity(fallback);
  }, [featureToggles, customContent, topic, activeActivity, getActivityAvailability]);

  const PresentationDeck = ({ slides }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const deckRef = useRef(null);
    const normalizedSlides = Array.isArray(slides) ? slides : [];

    const requestFullscreen = useCallback(() => {
      if (!deckRef.current) return;
      const el = deckRef.current;
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      } else if (el.msRequestFullscreen) {
        el.msRequestFullscreen();
      }
    }, []);

    const exitFullscreen = useCallback(() => {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }, []);

    useEffect(() => {
      const handleChange = () => {
        const activeEl = document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
        setIsFullscreen(Boolean(activeEl));
      };
      document.addEventListener('fullscreenchange', handleChange);
      document.addEventListener('webkitfullscreenchange', handleChange);
      document.addEventListener('msfullscreenchange', handleChange);
      return () => {
        document.removeEventListener('fullscreenchange', handleChange);
        document.removeEventListener('webkitfullscreenchange', handleChange);
        document.removeEventListener('msfullscreenchange', handleChange);
      };
    }, []);

    if (!normalizedSlides.length) {
      return null;
    }

    const totalSlides = normalizedSlides.length;
    const safeIndex = Math.min(currentSlide, totalSlides - 1);
    const slide = normalizedSlides[safeIndex] || {};

    return (
      <div className={`presentation-deck ${isFullscreen ? 'fullscreen-active' : ''}`} ref={deckRef}>
        <div className="slide-frame">
          <div className="slide-header">
            <span className="slide-count">Slide {safeIndex + 1} / {totalSlides}</span>
            {slide.subtitle && <span className="slide-subtitle">{slide.subtitle}</span>}
          </div>
          <h4 className="slide-title">{slide.title || `Slide ${safeIndex + 1}`}</h4>
          {(slide.bulletPoints || []).length > 0 && (
            <ul className="slide-bullets">
              {slide.bulletPoints.map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="slide-controls">
          <button
            type="button"
            onClick={() => setCurrentSlide((prev) => Math.max(0, prev - 1))}
            disabled={safeIndex === 0}
          >
            ← Previous
          </button>
          <button
            type="button"
            onClick={() => setCurrentSlide((prev) => Math.min(totalSlides - 1, prev + 1))}
            disabled={safeIndex === totalSlides - 1}
          >
            Next →
          </button>
          <button
            type="button"
            className="slide-fullscreen-btn"
            onClick={() => (isFullscreen ? exitFullscreen() : requestFullscreen())}
          >
            {isFullscreen ? '⤢ Exit Full Screen' : '⤢ Full Screen'}
          </button>
        </div>
      </div>
    );
  };

  // Quick interactive quiz for topic
  const QuickQuiz = ({ topic }) => {
    // Use custom content if available, otherwise generate default
    const questions = customContent?.quizQuestions?.length > 0 
      ? customContent.quizQuestions 
      : generateQuestionsForTopic(topic);
    
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);

        const handleAnswer = async (answer, correctAnswer) => {
          setSelectedAnswer(answer);
          // Handle both string answers and index-based correct answers
          const correct = typeof correctAnswer === 'number' 
            ? questions[currentQuestionIndex].options[correctAnswer] 
            : correctAnswer;
          const isAnswerCorrect = answer === correct;
          setIsCorrect(isAnswerCorrect);
          setShowResult(true);
          
          // Log quiz attempt
          await activityLogService.logActivity(userId, activityLogService.ACTIVITY_TYPES.QUIZ_ATTEMPT, {
            courseId,
            topicName: topic?.name,
            questionIndex: currentQuestionIndex,
            selectedAnswer: answer,
            correctAnswer: correct,
            isCorrect: isAnswerCorrect
          });
          
          if (isAnswerCorrect) {
            setScore(prev => prev + 10);
            setTimeout(() => {
              setShowResult(false);
              setSelectedAnswer(null);
              // Move to next question if available
              if (currentQuestionIndex < questions.length - 1) {
                setCurrentQuestionIndex(currentQuestionIndex + 1);
              }
            }, 2000);
          }
        };

    if (!questions || questions.length === 0) return null;

    const currentQuestion = questions[currentQuestionIndex] || questions[0];
    // Handle correct answer - could be index or string
    const correctAnswer = typeof currentQuestion.correct === 'number'
      ? currentQuestion.options[currentQuestion.correct]
      : currentQuestion.correct;

    return (
      <div className="quick-quiz">
        <h4>🎯 Quick Check: {topic.name}</h4>
        <div className="quiz-question">
          <p>{currentQuestion.question}</p>
          <div className="quiz-options">
            {currentQuestion.options.map((option, idx) => (
              <button
                key={idx}
                className={`quiz-option ${selectedAnswer === option ? (isCorrect ? 'correct' : 'wrong') : ''}`}
                onClick={() => handleAnswer(option, correctAnswer)}
                disabled={showResult}
              >
                {option}
              </button>
            ))}
          </div>
          {questions.length > 1 && (
            <div className="quiz-navigation">
              <p>Question {currentQuestionIndex + 1} of {questions.length}</p>
            </div>
          )}
          {showResult && (
            <div className={`quiz-result ${isCorrect ? 'correct' : 'wrong'}`}>
              {isCorrect ? '🎉 Great job! You got it right!' : '💭 Not quite! Try again!'}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Drag and drop matching activity
  const MatchingActivity = ({ topic }) => {
    // Use custom content if available, otherwise use defaults
    const defaultPairs = [
      { id: 1, text: 'Variable', match: 'Stores data' },
      { id: 2, text: 'Function', match: 'Reusable code' },
      { id: 3, text: 'Loop', match: 'Repeats code' },
    ];
    
    const customPairs = customContent?.matchingPairs?.map((pair, idx) => ({
      id: idx + 1,
      text: pair.item,
      match: pair.match
    })) || [];
    
    const initialItems = customPairs.length > 0 ? customPairs : defaultPairs;
    
    const [items] = useState(initialItems);
    const [draggedItem, setDraggedItem] = useState(null);
    const [matches, setMatches] = useState({});

    const handleDragStart = (item) => {
      setDraggedItem(item);
    };

    const handleDrop = async (matchText) => {
      if (draggedItem && draggedItem.match === matchText) {
        setMatches({ ...matches, [draggedItem.id]: matchText });
        setScore(prev => prev + 5);
        // Log matching completion
        await activityLogService.logActivity(userId, activityLogService.ACTIVITY_TYPES.MATCHING_COMPLETE, {
          courseId,
          topicName: topic?.name,
          item: draggedItem.text,
          match: matchText
        });
      }
      setDraggedItem(null);
    };

    return (
      <div className="matching-activity">
        <h4>🔗 Match the Concepts: {topic.name}</h4>
        <div className="matching-container">
          <div className="matching-items">
            {items.map(item => (
              <div
                key={item.id}
                className={`matching-item ${matches[item.id] ? 'matched' : ''}`}
                draggable={!matches[item.id]}
                onDragStart={() => handleDragStart(item)}
              >
                {item.text}
              </div>
            ))}
          </div>
          <div className="matching-targets">
            {items.map(item => (
              <div
                key={item.id}
                className={`matching-target ${matches[item.id] ? 'matched' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(item.match)}
              >
                {matches[item.id] ? '✓ ' : ''}{item.match}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Interactive code playground
  const CodePlayground = ({ topic }) => {
    // Use custom code examples if available, otherwise use topic sampleCode
    const defaultCode = topic.sampleCode || 'print("Hello, World!")';
    const customCode = customContent?.codeExamples?.length > 0 
      ? customContent.codeExamples[0].code 
      : null;
    
    const [code, setCode] = useState(customCode || defaultCode);
    const [output, setOutput] = useState('');
    const [isRunning, setIsRunning] = useState(false);

    const runCode = async () => {
      setIsRunning(true);
      setOutput('');

      setTimeout(async () => {
        const result = runPlaygroundCode(code);
        setOutput(result.output);

        try {
          await activityLogService.logActivity(userId, activityLogService.ACTIVITY_TYPES.CODE_PLAYGROUND_USE, {
            courseId,
            topicName: topic?.name,
            code: code.substring(0, 200),
            language: result.language
          });
        } catch (error) {
          console.debug('Failed to log playground activity', error);
        }

        setIsRunning(false);
      }, 150);
    };

    return (
      <div className="code-playground">
        <h4>💻 Try It Yourself: {topic.name}</h4>
        <div className="playground-editor">
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Write your code here..."
            className="code-input"
          />
          <button onClick={runCode} className="run-code-btn" disabled={isRunning}>
            {isRunning ? '⏳ Running...' : '▶️ Run Code'}
          </button>
        </div>
        {output && (
          <div className="playground-output">
            <strong>Output:</strong>
            <pre>{output}</pre>
          </div>
        )}
      </div>
    );
  };

  // Generate simple questions based on topic
  const generateQuestionsForTopic = (topic) => {
    const topicName = topic?.name?.toLowerCase() || '';
    
    if (topicName.includes('variable')) {
      return [{
        question: 'What does a variable do?',
        options: ['Stores data', 'Deletes files', 'Sends emails', 'Plays music'],
        correct: 'Stores data'
      }];
    }
    
    if (topicName.includes('loop')) {
      return [{
        question: 'What does a loop do?',
        options: ['Stops code', 'Repeats code', 'Deletes code', 'Saves code'],
        correct: 'Repeats code'
      }];
    }
    
    if (topicName.includes('function')) {
      return [{
        question: 'Why do we use functions?',
        options: ['To make code longer', 'To reuse code', 'To delete code', 'To hide code'],
        correct: 'To reuse code'
      }];
    }

    return [{
      question: `What did you learn about ${topic?.name || 'this topic'}?`,
      options: ['It\'s fun!', 'It\'s easy!', 'I understand it!', 'All of the above!'],
      correct: 'All of the above!'
    }];
  };

  const activityAvailability = getActivityAvailability();
  const activityButtons = ACTIVITY_ORDER
    .filter((key) => activityAvailability[key])
    .map((key) => ({ key, ...ACTIVITY_CONFIG[key] }));
  const hasActivities = activityButtons.length > 0;

  return (
    <div className="topic-interactive-learning">
      <div className="topic-learning-header">
        <h5>🎮 Interactive Learning</h5>
        {score > 0 && (
          <span className="topic-score">⭐ {score} stars</span>
        )}
      </div>
      <p className="topic-learning-subtitle">
        {isStudent 
          ? (hasActivities 
              ? 'View and interact with coding examples for this topic.'
              : 'No coding content available for this topic yet.')
          : (hasActivities
              ? 'Pick the interactive format that suits how you learn best.'
              : 'Interactive lessons are turned off for this session.')}
      </p>

      {/* Learning Cards - Only show for admin/coach, not students */}
      {!isStudent && featureToggles.enableLearningCards && customContent?.learningCards && customContent.learningCards.length > 0 && (
        <LearningCards
          courseId={courseId}
          topicName={topic?.name}
          userId={userId}
          userName={userName}
        />
      )}

      <div className="topic-activity-selector">
        {hasActivities ? (
          activityButtons.map((btn) => (
            <button
              key={btn.key}
              className={`activity-choice ${activeActivity === btn.key ? 'active' : ''}`}
              type="button"
              onClick={() => setActiveActivity(btn.key)}
              aria-pressed={activeActivity === btn.key}
            >
              <span className="choice-icon" aria-hidden="true">{btn.icon}</span>
              <div className="choice-details">
                <span className="choice-label">{btn.label}</span>
                <span className="choice-description">{btn.description}</span>
              </div>
              {activeActivity === btn.key && (
                <span className="choice-indicator">In Progress</span>
              )}
            </button>
          ))
        ) : (
          <p className="activity-disabled-note">
            ⚠️ Interactive modules are disabled for this session.
          </p>
        )}
      </div>

      {loadingContent ? (
        <div className="topic-activity-content loading-state">
          <p className="activity-loading-copy">Loading custom activities for this topic…</p>
        </div>
      ) : (
        <div className="topic-activity-content">
          {activeActivity && hasActivities && (
            <div className="active-activity-banner">
              <div>
                <span className="banner-label">{ACTIVITY_CONFIG[activeActivity]?.label || 'Activity'}</span>
                <p>{ACTIVITY_CONFIG[activeActivity]?.description}</p>
              </div>
              <span className="banner-status">Now playing</span>
            </div>
          )}
          {activeActivity === 'quiz' && activityAvailability.quiz && <QuickQuiz topic={topic} />}
          {activeActivity === 'exercises' && activityAvailability.exercises && (
            <QuickExercises
              topic={topic}
              customExercises={customContent?.exercises}
              userId={userId}
              courseId={courseId}
            />
          )}
          {activeActivity === 'matching' && activityAvailability.matching && <MatchingActivity topic={topic} />}
          {activeActivity === 'syntax' && activityAvailability.syntax && (
            <SyntaxGuessCard
              cards={customContent?.syntaxGuessCards}
              onPointsEarned={(points) => setScore(prev => prev + points)}
              userId={userId}
              courseId={courseId}
              topicName={topic?.name}
            />
          )}
          {activeActivity === 'code' && activityAvailability.code && (
            <CodePlayground topic={topic} />
          )}
          {activeActivity === 'coding' && (
            (customContent?.codingExamples?.length > 0 || customContent?.coding?.code) && (
              <CodingContentViewer 
                courseId={courseId} 
                topicName={topic?.name}
                userId={userId}
              />
            )
          )}
          {activeActivity === 'slides' && activityAvailability.slides && (
            <PresentationDeck slides={customContent.presentationSlides} />
          )}
          {!hasActivities && !customContent?.coding?.code ? (
            <div className="topic-activity-placeholder">
              <p>⚠️ Interactive activities are disabled for this session. Ask your coach to enable them if you want extra practice.</p>
            </div>
          ) : (
            !activeActivity && (
              <div className="topic-activity-placeholder">
                <p>👆 Choose an activity above to start learning! We’ll keep this space ready once you decide.</p>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default TopicInteractiveLearning;

